const INSTITUTIONAL_SOURCE_KINDS = new Set([
  'institutional',
  'jurisprudence',
  'legislation',
]);

export const REVIEW_FOLLOWUP_ACTION = Object.freeze({
  APPLY_APPROVED: 'apply_review_changes',
  REVIEW_PENDING: 'review_recommendations',
  VIEW_REVIEW: 'view_review',
});

export const REVIEW_FOLLOWUP_DISMISS_LABEL = 'Ahora no';

const asObject = value => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);

const cleanString = value => String(value == null ? '' : value).trim();

const cleanCount = value => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.floor(number);
};

const cleanIdentifierList = value => {
  if (!Array.isArray(value)) return Object.freeze([]);
  return Object.freeze(value.map(item => cleanString(item)));
};

const explicitBoolean = (value, fallback = false) => {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return fallback;
};

const singularOrPlural = (count, singular, plural) => (
  Number(count) === 1 ? singular : plural
);

const partiallyAppliedCopy = count => (
  `${count} ${singularOrPlural(
    count,
    'recomendación aplicada parcialmente',
    'recomendaciones aplicadas parcialmente',
  )}`
);

const appendPartialStatus = (copy, review) => (
  review.partiallyAppliedCount > 0
    ? `${copy} · aplicación parcial`
    : copy
);

const normalizedMaterialStateKey = review => JSON.stringify([
  review.reviewId,
  review.sourceKind,
  review.editable,
  // Opening an editable review is navigation, not a new decision state, so it
  // must not revive a reminder the user dismissed. For read-only content,
  // opening the workspace is the terminal state of the one-time reminder.
  review.editable ? false : review.workspaceOpened,
  review.approvedCount,
  review.approvedPendingCount,
  review.approvedPendingFindingIds,
  review.undecidedCount,
  review.partiallyAppliedCount,
  review.approvedChangesApplied,
  review.findingCount,
]);

const reviewAction = (id, label, review, extra = {}) => Object.freeze({
  id,
  label,
  reviewId: review.reviewId,
  explicit: true,
  ...extra,
});

const pendingAction = (review, label) => reviewAction(
  REVIEW_FOLLOWUP_ACTION.REVIEW_PENDING,
  label,
  review,
  {
    // Normal review entry always starts with the complete findings list.
    // "Por decidir" remains available as an explicit filter inside the workspace.
    workspaceMode: 'all',
    undecidedCount: review.undecidedCount,
  },
);

const applyAction = (review, label) => reviewAction(
  REVIEW_FOLLOWUP_ACTION.APPLY_APPROVED,
  label,
  review,
  {
    approvedCount: review.approvedPendingCount,
    applySnapshot: createReviewApplySnapshot(review),
  },
);

const viewAction = review => reviewAction(
  REVIEW_FOLLOWUP_ACTION.VIEW_REVIEW,
  'Ver revisión',
  review,
  { workspaceMode: 'all' },
);

/**
 * Normalize the persisted active-review summary used by the card, workspace,
 * composer, and chat routing. Counts remain server-authoritative: this helper
 * intentionally never derives decisions from a client-side findings array.
 */
export function normalizeActiveReview(rawReview) {
  if (!rawReview || typeof rawReview !== 'object' || Array.isArray(rawReview)) return null;

  const source = asObject(rawReview.source);
  const counts = asObject(rawReview.counts);
  const reminder = asObject(rawReview.reminder);
  const resultRef = asObject(rawReview.result_ref);

  const reviewId = cleanString(firstDefined(
    rawReview.review_id,
    rawReview.reviewId,
    rawReview.id,
    resultRef.id,
  ));
  if (!reviewId) return null;

  const sourceKind = cleanString(firstDefined(
    rawReview.source_kind,
    rawReview.review_subject_kind,
    source.kind,
    source.type,
  )).toLocaleLowerCase('es-ES') || 'attached_content';
  const reviewProfile = cleanString(firstDefined(
    rawReview.review_profile,
    rawReview.profile,
  )).toLocaleLowerCase('es-ES');
  const sourceIsInstitutional = (
    INSTITUTIONAL_SOURCE_KINDS.has(sourceKind)
    || reviewProfile === 'institutional'
  );
  const sourceIsReadOnly = reviewProfile === 'read_only';
  const requestedEditable = explicitBoolean(firstDefined(
    rawReview.review_subject_editable,
    rawReview.editable,
    rawReview.can_edit,
    source.editable,
  ));
  // Read-only sources must fail closed even if a stale or conflicting client
  // cache incorrectly claims that the source is editable.
  const editable = requestedEditable && !sourceIsInstitutional && !sourceIsReadOnly;

  const approvedCount = cleanCount(firstDefined(
    rawReview.approved_count,
    rawReview.approvedCount,
    rawReview.accepted_count,
    counts.approved,
    counts.accepted,
  ));
  const approvedChangesApplied = explicitBoolean(firstDefined(
    rawReview.approved_changes_applied,
    rawReview.approvedChangesApplied,
    rawReview.changes_applied,
    rawReview.applied,
  ));
  const rawApprovedPendingCount = firstDefined(
    rawReview.approved_pending_count,
    rawReview.approvedPendingCount,
    counts.approved_pending,
    counts.approvedPending,
  );
  const approvedPendingCount = rawApprovedPendingCount === undefined
    ? (approvedChangesApplied ? 0 : approvedCount)
    : Math.min(approvedCount, cleanCount(rawApprovedPendingCount));
  const approvedPendingFindingIds = cleanIdentifierList(firstDefined(
    rawReview.approved_pending_finding_ids,
    rawReview.approvedPendingFindingIds,
    rawReview.approvedFindingIds,
    counts.approved_pending_finding_ids,
  ));
  const undecidedCount = cleanCount(firstDefined(
    rawReview.undecided_count,
    rawReview.undecidedCount,
    rawReview.pending_count,
    rawReview.open_count,
    counts.undecided,
    counts.pending,
    counts.open,
  ));
  const partiallyAppliedCount = cleanCount(firstDefined(
    rawReview.partially_applied_count,
    rawReview.partiallyAppliedCount,
    counts.partially_applied,
    counts.partiallyApplied,
  ));
  const explicitFindingCount = firstDefined(
    rawReview.finding_count,
    rawReview.findingCount,
    rawReview.total_findings,
    counts.findings,
    counts.total,
  );
  const findingCount = explicitFindingCount === undefined
    ? cleanCount(counts.confirmed) + cleanCount(counts.potential)
    : cleanCount(explicitFindingCount);

  const normalized = {
    reviewId,
    sourceId: cleanString(firstDefined(
      rawReview.source_id,
      rawReview.sourceId,
      source.id,
    )),
    sourceTitle: cleanString(firstDefined(
      rawReview.source_title,
      rawReview.document_title,
      rawReview.document_name,
      source.title,
      source.name,
    )),
    sourceKind,
    editable,
    institutional: sourceIsInstitutional,
    workspaceOpened: explicitBoolean(firstDefined(
      rawReview.workspace_opened,
      rawReview.workspaceOpened,
    )),
    approvedCount,
    approvedPendingCount,
    approvedPendingFindingIds,
    undecidedCount,
    partiallyAppliedCount,
    findingCount,
    approvedChangesApplied,
    active: explicitBoolean(firstDefined(
      rawReview.is_active,
      rawReview.active,
    ), true),
    completionNoticePending: explicitBoolean(firstDefined(
      rawReview.completion_notice_pending,
      rawReview.completionNoticePending,
      rawReview.show_completion_notice,
    ), true),
    materialStateVersion: cleanString(firstDefined(
      rawReview.material_state_version,
      rawReview.materialStateVersion,
    )),
  };

  const derivedMaterialStateKey = normalizedMaterialStateKey(normalized);
  normalized.materialStateKey = cleanString(firstDefined(
    rawReview.material_state_key,
    rawReview.materialStateKey,
    rawReview.material_state,
    rawReview.materialState,
  )) || derivedMaterialStateKey;

  const reminderDismissed = explicitBoolean(firstDefined(
    rawReview.reminder_dismissed,
    rawReview.dismissed,
    reminder.dismissed,
  ));
  const dismissedMaterialStateKey = cleanString(firstDefined(
    rawReview.dismissed_material_state_key,
    rawReview.dismissedMaterialStateKey,
    reminder.material_state_key,
  ));
  const dismissedMaterialStateVersion = cleanString(firstDefined(
    rawReview.dismissed_material_state_version,
    rawReview.dismissedMaterialStateVersion,
    reminder.material_state_version,
  ));
  const stateChangedSinceDismissal = explicitBoolean(firstDefined(
    rawReview.state_changed_since_dismissal,
    rawReview.stateChangedSinceDismissal,
    reminder.state_changed,
  ));
  const dismissalKeyIsCurrent = (
    !dismissedMaterialStateKey
    || dismissedMaterialStateKey === normalized.materialStateKey
  );
  const dismissalVersionIsCurrent = (
    !dismissedMaterialStateVersion
    || !normalized.materialStateVersion
    || dismissedMaterialStateVersion === normalized.materialStateVersion
  );
  normalized.dismissed = reminderDismissed;
  normalized.dismissedForCurrentState = !!(
    reminderDismissed
    && !stateChangedSinceDismissal
    && dismissalKeyIsCurrent
    && dismissalVersionIsCurrent
  );

  return Object.freeze(normalized);
}

/**
 * Freeze the exact server projection behind an apply control. Consumers pass
 * this snapshot through unchanged so a later review update cannot retarget a
 * click, checkpoint, or workspace action to a different set of findings.
 */
export function createReviewApplySnapshot(rawReview) {
  const review = normalizeActiveReview(rawReview);
  if (!review) return null;
  return Object.freeze({
    reviewId: review.reviewId,
    materialState: review.materialStateKey,
    approvedFindingIds: Object.freeze([...review.approvedPendingFindingIds]),
    approvedCount: review.approvedPendingCount,
    undecidedCount: review.undecidedCount,
  });
}

export function reviewMaterialStateKey(rawReview) {
  const review = normalizeActiveReview(rawReview);
  return review ? review.materialStateKey : '';
}

const editableStatusCopy = review => {
  const applicableApproved = review.approvedPendingCount;
  if (review.undecidedCount > 0 && applicableApproved > 0) {
    return appendPartialStatus(
      `${applicableApproved} ${singularOrPlural(applicableApproved, 'aprobada', 'aprobadas')} · ${review.undecidedCount} por decidir`,
      review,
    );
  }
  if (review.undecidedCount > 0) {
    return appendPartialStatus(
      `${review.undecidedCount} ${singularOrPlural(review.undecidedCount, 'recomendación', 'recomendaciones')} por decidir`,
      review,
    );
  }
  if (applicableApproved > 0) {
    return appendPartialStatus(
      `${applicableApproved} ${singularOrPlural(applicableApproved, 'lista', 'listas')} para aplicar`,
      review,
    );
  }
  if (review.partiallyAppliedCount > 0) {
    return `${partiallyAppliedCopy(review.partiallyAppliedCount)} · completa los marcadores`;
  }
  return 'Revisión completada';
};

const editableCardExplanation = review => {
  if (review.partiallyAppliedCount > 0) {
    if (review.undecidedCount > 0 || review.approvedPendingCount > 0) {
      return 'Completa los marcadores pendientes y gestiona las recomendaciones restantes.';
    }
    return 'La versión guardada contiene marcadores para datos que debes proporcionar antes de considerar terminada la recomendación.';
  }
  return 'Elige cuáles quieres aplicar. Luego podrás actualizar el documento.';
};

const editableCardActions = review => {
  const applicableApproved = review.approvedPendingCount;
  if (review.undecidedCount > 0) {
    const actions = [];
    if (applicableApproved > 0) {
      actions.push(applyAction(review, `Aplicar ${applicableApproved}`));
    }
    actions.push(pendingAction(review, 'Revisar recomendaciones'));
    return Object.freeze(actions);
  }
  if (applicableApproved > 0) {
    return Object.freeze([
      applyAction(review, 'Actualizar documento'),
      viewAction(review),
    ]);
  }
  // Applying or deciding every finding ends the follow-up reminder, but the
  // originating card remains the durable entry point to the full review.
  return Object.freeze([viewAction(review)]);
};

const editableBar = review => {
  const applicableApproved = review.approvedPendingCount;
  let copy = 'Revisión completada';
  let actions = [];
  let transient = false;

  if (review.undecidedCount > 0 && applicableApproved > 0) {
    copy = appendPartialStatus(
      `${applicableApproved} ${singularOrPlural(applicableApproved, 'lista', 'listas')} para aplicar · ${review.undecidedCount} por decidir`,
      review,
    );
    actions = [
      applyAction(review, `Aplicar ${applicableApproved}`),
      pendingAction(review, 'Revisar'),
    ];
  } else if (review.undecidedCount > 0) {
    copy = appendPartialStatus(
      `${review.undecidedCount} ${singularOrPlural(review.undecidedCount, 'recomendación', 'recomendaciones')} por decidir`,
      review,
    );
    actions = [pendingAction(review, 'Revisar')];
  } else if (applicableApproved > 0) {
    copy = appendPartialStatus(
      `${applicableApproved} ${singularOrPlural(applicableApproved, 'recomendación lista', 'recomendaciones listas')} para aplicar`,
      review,
    );
    actions = [applyAction(review, 'Actualizar documento')];
  } else if (review.partiallyAppliedCount > 0) {
    copy = `${partiallyAppliedCopy(review.partiallyAppliedCount)} · completa los marcadores`;
    actions = [viewAction(review)];
  } else {
    transient = true;
  }

  const relevant = review.undecidedCount > 0
    || applicableApproved > 0
    || review.partiallyAppliedCount > 0;
  const completionCanShow = transient && review.completionNoticePending;
  return Object.freeze({
    visible: !!(
      review.active
      && !review.dismissedForCurrentState
      && (relevant || completionCanShow)
    ),
    transient,
    copy,
    actions: Object.freeze(actions),
    dismissible: true,
    dismissLabel: REVIEW_FOLLOWUP_DISMISS_LABEL,
  });
};

const institutionalBar = review => Object.freeze({
  visible: !!(
    review.active
    && !review.workspaceOpened
    && !review.dismissedForCurrentState
  ),
  transient: false,
  copy: `Revisión lista · ${review.findingCount} ${singularOrPlural(review.findingCount, 'hallazgo', 'hallazgos')}`,
  actions: Object.freeze([viewAction(review)]),
  dismissible: true,
  dismissLabel: REVIEW_FOLLOWUP_DISMISS_LABEL,
});

/**
 * Derive the coordinated composer-bar and original-card presentation. This is
 * deliberately presentation-only: it never examines or classifies free text.
 */
export function getReviewFollowupPresentation(rawReview) {
  const review = normalizeActiveReview(rawReview);
  if (!review) {
    return Object.freeze({
      review: null,
      relevant: false,
      requiresIntentResolution: false,
      dismissed: false,
      bar: Object.freeze({
        visible: false,
        transient: false,
        copy: '',
        actions: Object.freeze([]),
        dismissible: false,
        dismissLabel: REVIEW_FOLLOWUP_DISMISS_LABEL,
      }),
      card: null,
    });
  }

  if (!review.editable) {
    return Object.freeze({
      review,
      relevant: !!(review.active && !review.workspaceOpened),
      requiresIntentResolution: false,
      dismissed: review.dismissedForCurrentState,
      bar: institutionalBar(review),
      card: Object.freeze({
        status: `Revisión lista · ${review.findingCount} ${singularOrPlural(review.findingCount, 'hallazgo', 'hallazgos')}`,
        explanation: 'Abre la revisión para ver los hallazgos.',
        actions: Object.freeze([viewAction(review)]),
      }),
    });
  }

  const applicableApproved = review.approvedPendingCount;
  return Object.freeze({
    review,
    relevant: !!(
      review.active
      && (
        review.undecidedCount > 0
        || applicableApproved > 0
        || review.partiallyAppliedCount > 0
      )
    ),
    // Partial applications remain visible, but completing their missing data
    // is an ordinary chat turn rather than another review decision.
    requiresIntentResolution: !!(
      review.active && (review.undecidedCount > 0 || applicableApproved > 0)
    ),
    dismissed: review.dismissedForCurrentState,
    bar: editableBar(review),
    card: Object.freeze({
      status: editableStatusCopy(review),
      explanation: editableCardExplanation(review),
      actions: editableCardActions(review),
    }),
  });
}
